import { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Lock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

// Validation functions
const validateName = (name) => {
  if (!name || name.trim().length < 2) {
    return 'Please enter your full name (at least 2 characters)';
  }
  if (!/^[a-zA-Z\s'-]+$/.test(name)) {
    return 'Name can only contain letters, spaces, hyphens, and apostrophes';
  }
  return '';
};

const validateEmail = (email) => {
  if (!email) {
    return 'Email is required';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Please enter a valid email address';
  }
  return '';
};

const validatePhone = (phone) => {
  if (!phone) {
    return 'Phone number is required';
  }
  // Remove non-digits for validation
  const digitsOnly = phone.replace(/\D/g, '');
  if (digitsOnly.length < 10) {
    return 'Please enter a valid phone number (at least 10 digits)';
  }
  return '';
};

export const ReferralForm = ({ onSubmit, isSubmitting, contractorName }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    bestTime: ''
  });
  
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Validate on blur
    let error = '';
    switch (field) {
      case 'name':
        error = validateName(formData.name);
        break;
      case 'email':
        error = validateEmail(formData.email);
        break;
      case 'phone':
        error = validatePhone(formData.phone);
        break;
      default:
        break;
    }
    
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate all required fields
    const nameError = validateName(formData.name);
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);
    
    const newErrors = {
      name: nameError,
      email: emailError,
      phone: phoneError
    };
    
    setErrors(newErrors);
    setTouched({ name: true, email: true, phone: true });
    
    // Check if any errors exist
    if (nameError || emailError || phoneError) {
      return;
    }
    
    // Format phone number (keep only digits)
    const formattedPhone = formData.phone.replace(/\D/g, '');
    
    // Submit the form
    onSubmit({
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formattedPhone,
      address: formData.city.trim(),
      notes: formData.bestTime ? `Best time to contact: ${formData.bestTime}` : ''
    });
  };

  const inputBaseStyles = "w-full h-12 px-4 rounded-lg bg-white border border-[#E1DFD7] text-[#0B2240] placeholder:text-[#4C5A70]/60 focus:ring-2 focus:ring-[#1FA3A3] focus:border-[#1FA3A3] transition-colors";
  const errorStyles = "border-red-400 focus:ring-red-400 focus:border-red-400";

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="show"
      className="rounded-2xl p-6 sm:p-8 border border-white/15 bg-white/10 backdrop-blur-xl"
      data-testid="referral-form-card"
    >
      {/* Form Header */}
      <div className="text-center mb-6">
        <p className="text-[#E3C96E] text-sm font-medium mb-2" data-testid="form-referrer-label">
          Referred by {contractorName}
        </p>
        <h3 
          className="font-['Playfair_Display'] text-xl sm:text-2xl text-white"
          data-testid="form-headline"
        >
          Let's Get Started
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="referral-lead-form">
        {/* Full Name */}
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-white/90 text-sm font-medium">
            Full Name <span className="text-[#D4AF37]">*</span>
          </Label>
          <Input
            id="name"
            type="text"
            placeholder="Enter your full name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            disabled={isSubmitting}
            className={`${inputBaseStyles} ${touched.name && errors.name ? errorStyles : ''}`}
            style={{ fontSize: '16px' }}
            data-testid="form-name-input"
            aria-invalid={touched.name && errors.name ? 'true' : 'false'}
            aria-describedby={errors.name ? 'name-error' : undefined}
          />
          {touched.name && errors.name && (
            <p id="name-error" className="text-red-400 text-xs mt-1" data-testid="form-name-error">
              {errors.name}
            </p>
          )}
        </div>

        {/* Email */}
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-white/90 text-sm font-medium">
            Email <span className="text-[#D4AF37]">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            disabled={isSubmitting}
            className={`${inputBaseStyles} ${touched.email && errors.email ? errorStyles : ''}`}
            style={{ fontSize: '16px' }}
            data-testid="form-email-input"
            aria-invalid={touched.email && errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {touched.email && errors.email && (
            <p id="email-error" className="text-red-400 text-xs mt-1" data-testid="form-email-error">
              {errors.email}
            </p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <Label htmlFor="phone" className="text-white/90 text-sm font-medium">
            Phone <span className="text-[#D4AF37]">*</span>
          </Label>
          <Input
            id="phone"
            type="tel"
            placeholder="(555) 123-4567"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            onBlur={() => handleBlur('phone')}
            disabled={isSubmitting}
            className={`${inputBaseStyles} ${touched.phone && errors.phone ? errorStyles : ''}`}
            style={{ fontSize: '16px' }}
            data-testid="form-phone-input"
            aria-invalid={touched.phone && errors.phone ? 'true' : 'false'}
            aria-describedby={errors.phone ? 'phone-error' : undefined}
          />
          {touched.phone && errors.phone && (
            <p id="phone-error" className="text-red-400 text-xs mt-1" data-testid="form-phone-error">
              {errors.phone}
            </p>
          )}
        </div>

        {/* City (Optional) */}
        <div className="space-y-1.5">
          <Label htmlFor="city" className="text-white/90 text-sm font-medium">
            City <span className="text-white/50 text-xs">(optional)</span>
          </Label>
          <Input
            id="city"
            type="text"
            placeholder="Houston, TX"
            value={formData.city}
            onChange={(e) => handleChange('city', e.target.value)}
            disabled={isSubmitting}
            className={inputBaseStyles}
            style={{ fontSize: '16px' }}
            data-testid="form-city-input"
          />
        </div>

        {/* Best Time (Optional) */}
        <div className="space-y-1.5">
          <Label htmlFor="bestTime" className="text-white/90 text-sm font-medium">
            Best time to contact <span className="text-white/50 text-xs">(optional)</span>
          </Label>
          <Select data-testid="referral-form-select-1"
            value={formData.bestTime}
            onValueChange={(value) => handleChange('bestTime', value)}
            disabled={isSubmitting}
          >
            <SelectTrigger 
              id="bestTime"
              className="w-full h-12 px-4 rounded-lg bg-white border border-[#E1DFD7] text-[#0B2240] focus:ring-2 focus:ring-[#1FA3A3]"
              style={{ fontSize: '16px' }}
              data-testid="form-besttime-select"
            >
              <SelectValue placeholder="Select a time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="morning" data-testid="form-besttime-morning">Morning (9am - 12pm)</SelectItem>
              <SelectItem value="afternoon" data-testid="form-besttime-afternoon">Afternoon (12pm - 5pm)</SelectItem>
              <SelectItem value="evening" data-testid="form-besttime-evening">Evening (5pm - 8pm)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-14 mt-6 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold text-base shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-[0.98] focus-visible:ring-2 focus-visible:ring-[#1FA3A3] disabled:opacity-70 disabled:cursor-not-allowed transition-all"
          data-testid="form-submit-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-5 w-5 mr-2" />
              Get Started
            </>
          )}
        </Button>

        {/* Privacy Notice */}
        <p 
          className="text-center text-white/50 text-xs mt-4 flex items-center justify-center gap-1.5"
          data-testid="form-privacy-notice"
        >
          <Lock className="h-3 w-3" />
          Your information is secure and confidential
        </p>
      </form>
    </motion.div>
  );
};

export default ReferralForm;
